import { useState } from "react";
import { Link } from "react-router";
import {
  ClipboardCheck,
  AlertCircle,
  ArrowRight,
  BarChart3,
  Filter,
  Layers3,
  Package,
  Search,
  ShieldCheck,
  Sparkles,
  Star,
} from "lucide-react";
import { useMarketplaceBrowse, type MarketplacePackage } from "@/hooks/use-marketplace";
import { useDebounce } from "@/hooks/use-debounce";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import { useAuth } from "@/lib/auth-provider";
import { formatCurrency, formatDate, formatNumber, formatPercent } from "@/lib/i18n";

function MarketplaceSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-10 rounded-lg" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }, (_, index) => (
          <SkeletonCard key={index} className="h-72" />
        ))}
      </div>
    </div>
  );
}

function formatPackageComposition(pkg: MarketplacePackage) {
  const assetEntries = Object.entries(pkg.packageSummary.assetCounts)
    .filter(([, count]) => count > 0)
    .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))
    .slice(0, 3);

  if (assetEntries.length === 0) {
    return "Package composition is available on the detail page.";
  }

  return assetEntries
    .map(([assetType, count]) => `${count} ${assetType}`)
    .join(" · ");
}

function PackageCard({ pkg }: { pkg: MarketplacePackage }) {
  const priceLabel = pkg.priceCents === 0
    ? "Free"
    : formatCurrency(pkg.priceCents, pkg.currency, { maximumFractionDigits: 0 });

  const actionLabel = (() => {
    switch (pkg.installState.status) {
      case "already_purchased":
        return "Resume install";
      case "checkout_required":
        return "Review & checkout";
      case "install_available":
        return "Review & install";
      case "already_deployed":
        return "View installed state";
      default:
        return "Review package";
    }
  })();

  const stateHint = (() => {
    switch (pkg.installState.status) {
      case "already_purchased":
        return "This tenant already owns the package. Open the detail view to finish deployment on the mounted install route.";
      case "already_deployed":
        return "This tenant already deployed the package. Open the detail view to inspect the installed marketplace state.";
      default:
        return pkg.installState.description;
    }
  })();

  const performanceSummary = pkg.performancePreview?.isVerified
    ? [
      pkg.performancePreview.avgConversionRatePercent !== null
        ? `${formatPercent(pkg.performancePreview.avgConversionRatePercent)} conv`
        : null,
      pkg.performancePreview.avgEmailOpenRatePercent !== null
        ? `${formatPercent(pkg.performancePreview.avgEmailOpenRatePercent)} opens`
        : null,
      pkg.performancePreview.avgRoas !== null
        ? `${formatNumber(pkg.performancePreview.avgRoas)}x ROAS`
        : null,
    ].filter(Boolean).join(" · ")
    : null;
  const verifiedSampleLabel = pkg.performancePreview?.isVerified
    ? `${formatNumber(pkg.performancePreview.sampleSize)} verified install${pkg.performancePreview.sampleSize === 1 ? "" : "s"}`
    : null;

  return (
    <div className="stagger-item group overflow-hidden rounded-xl border border-border bg-background transition-shadow hover:shadow-md">
      <div className="relative flex h-36 items-center justify-center bg-gradient-to-br from-[var(--color-primary)]/10 via-[var(--color-ai)]/5 to-[var(--color-warning)]/10">
        <Package className="h-12 w-12 text-[var(--color-primary)]/35" />
        <span className="absolute left-3 top-3 rounded-full border border-border/70 bg-background/90 px-2 py-0.5 text-[10px] font-medium text-foreground">
          {pkg.packageSummary.totalAssets} assets
        </span>
        <span className="absolute right-3 top-3 rounded-full border border-border/70 bg-background/90 px-2 py-0.5 text-[10px] font-medium text-foreground">
          {pkg.installState.label}
        </span>
      </div>

      <div className="space-y-4 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="truncate font-medium text-foreground">{pkg.title}</h3>
            <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
              <span>{pkg.creatorName}</span>
              {pkg.performancePreview?.isVerified ? (
                <ShieldCheck className="h-3 w-3 text-[var(--color-primary)]" />
              ) : null}
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm font-medium text-foreground">
              {priceLabel}
            </div>
            <div className="text-[11px] text-muted-foreground">{pkg.category ?? "General"}</div>
          </div>
        </div>

        <p className="line-clamp-2 text-xs text-muted-foreground">
          {pkg.description ?? "Published package with mounted marketplace metadata."}
        </p>

        <div className="flex flex-wrap gap-1">
          {pkg.packageSummary.highlights.map((highlight) => (
            <span key={highlight} className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
              {highlight}
            </span>
          ))}
          {pkg.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="rounded-full border border-border px-2 py-0.5 text-[10px] text-muted-foreground">
              {tag}
            </span>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-2 rounded-lg bg-muted/40 p-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <Star className="h-3.5 w-3.5 fill-amber-400 text-[var(--color-warning)]/90" />
            <span>{formatNumber(pkg.avgRating)} ({formatNumber(pkg.reviewCount)})</span>
          </div>
          <div className="flex items-center gap-1.5">
            <BarChart3 className="h-3.5 w-3.5" />
            <span>{formatNumber(pkg.installCount)} installs</span>
          </div>
          <div className="col-span-2 text-[11px]">
            {performanceSummary
              ? `Verified performance: ${performanceSummary} · ${verifiedSampleLabel}`
              : pkg.performancePreview?.isVerified
                ? `Verified install sample: ${verifiedSampleLabel}`
                : "No verified conversion, email, or ROAS preview is mounted for this package yet."}
          </div>
          <div className="col-span-2 text-[11px]">
            {pkg.previewState.description}
          </div>
          <div className="col-span-2 text-[11px]">
            Includes {formatPackageComposition(pkg)}
          </div>
          {pkg.publishedAt ? (
            <div className="col-span-2 text-[11px]">
              Published {formatDate(pkg.publishedAt)}
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-3">
          <p className="min-w-0 flex-1 text-[11px] text-muted-foreground">
            {stateHint}
          </p>
          <Link
            to={`/marketplace/${pkg.id}`}
            className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-[var(--color-primary)] px-3 py-2 text-xs font-medium text-white transition-colors hover:bg-[var(--color-primary)]/90"
          >
            {actionLabel}
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function MarketplaceBrowser() {
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);
  const { data, isLoading, error } = useMarketplaceBrowse(selectedCategory, debouncedSearch || undefined);
  const { user } = useAuth();
  const canReviewMarketplace = user?.role === "admin" || user?.role === "super_admin";
  const showFeaturedPackages = Boolean(data?.featured.length) && !selectedCategory && !debouncedSearch;

  if (isLoading) return <MarketplaceSkeleton />;

  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-4 text-sm text-[var(--color-destructive)]">
        <AlertCircle className="h-4 w-4" />
        Failed to load marketplace.
      </div>
    );
  }

  if (!data) {
    return (
      <EmptyState
        icon={<Package className="h-8 w-8" />}
        title="No marketplace packages yet"
        description="This catalog is wired to the live marketplace listing service. Published packages will appear here."
        variant="info"
      />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Marketplace</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Review real builder packages from the mounted marketplace service. Browse shows verified demand signals, package contents, and whether a package installs directly or requires hosted checkout.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <Link
            to="/marketplace/creator"
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
          >
            <Layers3 className="h-4 w-4 text-[var(--color-primary)]" />
            Open creator dashboard
          </Link>
          {canReviewMarketplace ? (
            <Link
              to="/admin/marketplace-review"
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
            >
              <ClipboardCheck className="h-4 w-4 text-[var(--color-warning)]" />
              Open review queue
            </Link>
          ) : null}
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder="Search categories, creators, or package contents..."
          className="w-full rounded-lg border border-border bg-background py-2.5 pl-10 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
        />
      </div>

      {showFeaturedPackages ? (
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[var(--color-warning)]" />
            <div>
              <h2 className="text-lg font-medium text-foreground">Featured marketplace packages</h2>
              <p className="text-sm text-muted-foreground">
                Highlighted directly from the mounted marketplace browse contract for the default catalog view.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {data.featured.map((pkg) => (
              <PackageCard key={`featured-${pkg.id}`} pkg={pkg} />
            ))}
          </div>
        </section>
      ) : null}

      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        <Filter className="h-4 w-4 shrink-0 text-muted-foreground" />
        <button
          type="button"
          onClick={() => setSelectedCategory(undefined)}
          className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
            !selectedCategory
              ? "bg-[var(--color-primary)] text-white"
              : "bg-muted text-muted-foreground hover:text-foreground"
          }`}
        >
          All
        </button>
        {data.categories.map((category) => (
          <button
            key={category.name}
            type="button"
            onClick={() => setSelectedCategory(category.name)}
            className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              selectedCategory === category.name
                ? "bg-[var(--color-primary)] text-white"
                : "bg-muted text-muted-foreground hover:text-foreground"
            }`}
          >
            {category.name} ({category.count})
          </button>
        ))}
      </div>

      {data.featured.length > 0 && !searchQuery && !selectedCategory ? (
        <div>
          <h2 className="mb-3 flex items-center gap-2 text-lg font-medium text-foreground">
            <Sparkles className="h-5 w-5 text-[var(--color-ai)]" />
            Featured builder packages
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data.featured.map((pkg) => (
              <PackageCard key={pkg.id} pkg={pkg} />
            ))}
          </div>
        </div>
      ) : null}

      <div>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-medium text-foreground">
            {selectedCategory ?? "All Packages"} ({data.total})
          </h2>
        </div>

        {data.packages.length === 0 ? (
          <p className="py-12 text-center text-sm text-muted-foreground">
            No packages found matching your criteria.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data.packages.map((pkg) => (
              <PackageCard key={pkg.id} pkg={pkg} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
